import classnames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import ColorPicker from "web/react/components/color-picker/color-picker";
import OOSBuyOptionsRail from "web/react/components/oos-buy-options-rail/oos-buy-options-rail";
import OOSHero from "web/react/components/oos-hero/oos-hero";
import OOSImage from "web/react/components/oos-image/oos-image";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { gettext } from "web/script/modules/django-i18n";
import { languageIsEnglish } from "web/script/modules/language";
import userProfiler from "web/script/modules/userprofiler";
import { BuyOptionPropType } from "web/types/buy-option";
import { ColorVariantPropType } from "web/types/color-variant";
import { DesignerPropType } from "web/types/designer";
import { ProductPropType } from "web/types/product";
import { RelatedProductsSectionPropType } from "web/types/related-products";
import styles from "./oos-product-area.css";

export const OOSProductAreaPropType = PropTypes.shape({
    absolute_url: PropTypes.string.isRequired,
    buy_options: PropTypes.objectOf(BuyOptionPropType).isRequired,
    color_variants: PropTypes.arrayOf(ColorVariantPropType),
    designer: DesignerPropType.isRequired,
    is_saved_for_later: PropTypes.bool.isRequired,
    products: PropTypes.objectOf(ProductPropType).isRequired,
    sorted_buy_options: PropTypes.arrayOf(PropTypes.string).isRequired,
    sorted_variants: PropTypes.arrayOf(PropTypes.string).isRequired,
    stores_checked: PropTypes.number.isRequired,
    previously_seen_title: PropTypes.string.isRequired,
});

function OOSProductArea({
    className,
    productArea: {
        buy_options: buyOptions,
        products,
        designer,
        sorted_buy_options: allSortedBuyOptions,
        sorted_variants: sortedVariants,
        previously_seen_title: previouslySeenTitle,
        stores_checked: storesChecked,
    },
    relatedProductsSection,
    selectedVariant,
}) {
    let product = products[sortedVariants[0]];
    let sortedBuyOptions = allSortedBuyOptions.filter((linkId) =>
        product.link_ids.includes(linkId)
    );
    let isLoggedIn = React.useMemo(() => userProfiler.isLoggedIn(), []);

    let inStockVariants = [];

    if (sortedVariants.length > 1) {
        inStockVariants = sortedVariants.filter((variant) => {
            let product = products[variant];
            let sortedBuyOptions = allSortedBuyOptions.filter((linkId) =>
                product.link_ids.includes(linkId)
            );
            let buyOption = buyOptions[sortedBuyOptions[0]];
            return buyOption.in_stock;
        });
    }

    let showColorVariants = inStockVariants.length > 1;

    let galleryRelatedProducts = [];
    if (relatedProductsSection) {
        galleryRelatedProducts = relatedProductsSection.related_products.slice(0, 4);
    }

    return (
        <div className={classnames(styles.wrapper, className)}>
            <OOSHero
                numStores={storesChecked}
                designer={designer}
                seeSimilarLink={
                    relatedProductsSection ? relatedProductsSection.view_more_url : undefined
                }
            />
            <div className={styles.descriptionAndColorPicker}>
                <div className={styles.descriptionAndImage}>
                    <div className={styles.smallImage}>
                        <OOSImage product={product} relatedProducts={galleryRelatedProducts} />
                    </div>
                    <span className={styles.description}>
                        <h1>
                            {/* This is h1 in jinja file, important for SEO */}
                            <span className={styles.designerName}>
                                {designer.active && designer.url ? (
                                    <a href={designer.url} className={styles.designerLink}>
                                        {designer.name}
                                    </a>
                                ) : (
                                    <span>{designer.name}</span>
                                )}
                            </span>{" "}
                            <span className={styles.shortDescription}>
                                {product.short_description}
                            </span>
                        </h1>
                        {languageIsEnglish() && (
                            <SaveForLaterButton
                                cta={gettext("oos_product.get_stock_alerts_btn")} // Get stock alerts
                                type="stockAlert"
                                pageSource="oos"
                                isLoggedIn={isLoggedIn}
                                productId={product.product_id}
                            />
                        )}
                    </span>
                </div>
                {showColorVariants && (
                    <div className={styles.colorPickerArea}>
                        <h3 className={styles.title}>
                            {// "Try another color?"
                            gettext("oos_product.try_another_color.title")}
                        </h3>

                        <ColorPicker
                            allSortedBuyOptions={allSortedBuyOptions}
                            buyOptions={buyOptions}
                            current={selectedVariant}
                            className={styles.colorPicker}
                            variants={inStockVariants}
                            products={products}
                            variantLimit={8}
                        />
                    </div>
                )}
            </div>
            <OOSBuyOptionsRail
                className={styles.buyOptions}
                buyOptions={buyOptions}
                sortedBuyOptions={sortedBuyOptions}
                previouslySeenTitle={previouslySeenTitle}
            />
        </div>
    );
}

OOSProductArea.propTypes = {
    className: PropTypes.string,
    productArea: OOSProductAreaPropType.isRequired,
    relatedProductsSection: RelatedProductsSectionPropType,
    selectedVariant: PropTypes.string.isRequired,
};

export default OOSProductArea;
